import { useState, useCallback, useEffect } from 'react';
import { useApi, useToaster } from '../../../../utils/hooks';
import { ToastStyle } from '../../../../utils/hooks/useToaster';
import {
  IAnnualInvoiceDto,
  IAnnualInvoiceAddRequest,
  IAnnualInvoiceResult,
  IClubMemberDto
} from '../../../../utils/constants/interfaces';
import { SortDirection, MemberTitle, MemberSuffix } from '../../../../utils/constants/enums';
import { Helpers, getCurrentBillingYear } from '../../../../utils/helpers';

type InvoiceFormMode = 'disabled' | 'edit';

interface UseInvoicesProps {
  clubMemberId: string;
  fieldErrors: string[];
  setFieldErrors: React.Dispatch<React.SetStateAction<string[]>>;
  clubMember?: IClubMemberDto; // Club member data for printing
}

export const useInvoices = ({
  clubMemberId,
  fieldErrors,
  setFieldErrors,
  clubMember
}: UseInvoicesProps) => {
  const api = useApi();
  const toaster = useToaster();

  // State for invoices
  const [invoices, setInvoices] = useState<IAnnualInvoiceDto[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState<boolean>(false);
  const [savingInvoice, setSavingInvoice] = useState<boolean>(false);
  const [generatingInvoice, setGeneratingInvoice] = useState<boolean>(false);
  const [invoiceFormMode, setInvoiceFormMode] = useState<InvoiceFormMode>('disabled');
  const [editingInvoiceId, setEditingInvoiceId] = useState<number | null>(null);
  const [selectedInvoiceRow, setSelectedInvoiceRow] = useState<number | null>(null);

  // Invoice sorting
  const [invoiceSortField, setInvoiceSortField] = useState<string>('billingYear');
  const [invoiceSortDirection, setInvoiceSortDirection] = useState<SortDirection>(
    SortDirection.DESCENDING
  );

  const [invoiceFormData, setInvoiceFormData] = useState<{
    id: string;
    billingYear: string;
    totalAmount: string;
    dues: string;
    lateFee: string;
    initiationFee: string;
    otherFee: string;
    workHours: string;
    memberTypeName: string;
    memberTypeAmount: string;
    discountApplied: string;
    discountReason: string;
    notes: string;
  }>({
    id: '',
    billingYear: getCurrentBillingYear().toString(),
    totalAmount: '0.00',
    dues: '0.00',
    lateFee: '0.00',
    initiationFee: '0.00',
    otherFee: '0.00',
    workHours: '0',
    memberTypeName: '',
    memberTypeAmount: '0.00',
    discountApplied: '0.00',
    discountReason: '',
    notes: ''
  });

  // Calculate total amount whenever form data changes
  const calculateTotalAmount = useCallback(() => {
    // Helper function to safely parse values, treating blank as 0
    const safeParseFloat = (value: string): number => {
      if (value === '' || value === null || value === undefined) {
        return 0;
      }
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    };

    const dues = safeParseFloat(invoiceFormData.dues);
    const lateFee = safeParseFloat(invoiceFormData.lateFee);
    const initiationFee = safeParseFloat(invoiceFormData.initiationFee);
    const otherFee = safeParseFloat(invoiceFormData.otherFee);
    const discount = safeParseFloat(invoiceFormData.discountApplied);

    const total = dues + lateFee + initiationFee + otherFee;
    return total.toFixed(2);
  }, [
    invoiceFormData.dues,
    invoiceFormData.lateFee,
    invoiceFormData.initiationFee,
    invoiceFormData.otherFee,
    invoiceFormData.discountApplied
  ]);

  // Update total amount when relevant fields change
  useEffect(() => {
    const newTotal = calculateTotalAmount();
    setInvoiceFormData((prev) => ({
      ...prev,
      totalAmount: newTotal
    }));
  }, [calculateTotalAmount]);

  // Fetch invoices from API
  const fetchInvoices = useCallback(
    async (memberId: string): Promise<void> => {
      if (!memberId || memberId === 'new') {
        setInvoices([]);
        return;
      }

      setLoadingInvoices(true);

      try {
        const endpoint = `/members/${memberId}/invoices`;
        const apiResponse = await api.get<IAnnualInvoiceDto[]>(endpoint);

        if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
          // Process dates in invoices data
          const processedInvoices = apiResponse.response
            .map((invoice) => {
              const createdDate = Helpers.convertToDate(invoice.dateCreatedUtc);
              const modifiedDate = invoice.lastModifiedDateUtc
                ? Helpers.convertToDate(invoice.lastModifiedDateUtc)
                : null;

              return {
                ...invoice,
                dateCreatedUtc: createdDate || new Date(),
                lastModifiedDateUtc: modifiedDate
              };
            })
            .filter((invoice) => {
              // Filter out any entries where critical dates couldn't be parsed
              const hasValidCreatedDate =
                invoice.dateCreatedUtc && !isNaN(invoice.dateCreatedUtc.getTime());
              return hasValidCreatedDate;
            });

          setInvoices(processedInvoices);
        } else {
          setInvoices([]);
          toaster.addToast('Error fetching invoices', ToastStyle.ERROR, 3000);
        }
      } catch (error) {
        setInvoices([]);
        toaster.addToast('Error fetching invoices', ToastStyle.ERROR, 3000);
      } finally {
        setLoadingInvoices(false);
      }
    },
    [api, toaster]
  );

  // Load invoices when club member ID changes
  useEffect(() => {
    if (clubMemberId && clubMemberId !== 'new') {
      void fetchInvoices(clubMemberId);
    } else {
      setInvoices([]);
    }
  }, [clubMemberId]); // Removed fetchInvoices from dependencies to prevent infinite loop

  const updateInvoiceFormData = (field: string, value: string) => {
    if (savingInvoice || generatingInvoice) return;

    setInvoiceFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Clear related field errors when user updates data
    setFieldErrors((prev) => prev.filter((error) => !error.startsWith(`invoices:${field}`)));
  };

  const clearInvoiceForm = () => {
    setInvoiceFormData({
      id: '',
      billingYear: getCurrentBillingYear().toString(),
      totalAmount: '0.00',
      dues: '0.00',
      lateFee: '0.00',
      initiationFee: '0.00',
      otherFee: '0.00',
      workHours: '0',
      memberTypeName: '',
      memberTypeAmount: '0.00',
      discountApplied: '0.00',
      discountReason: '',
      notes: ''
    });
    setInvoiceFormMode('disabled');
    setEditingInvoiceId(null);
    setSelectedInvoiceRow(null);
    setFieldErrors((prev) => prev.filter((error) => !error.startsWith('invoices:')));
  };

  const selectInvoiceForEdit = (invoice: IAnnualInvoiceDto, rowIndex: number) => {
    // Populate form with existing data
    const totalAmount = (
      invoice.dues +
      invoice.lateFee +
      invoice.initiationFee +
      invoice.otherFee
    ).toFixed(2);

    setInvoiceFormData({
      id: invoice.id.toString(),
      billingYear: invoice.billingYear.toString(),
      totalAmount: totalAmount,
      dues: invoice.dues.toString(),
      lateFee: invoice.lateFee.toString(),
      initiationFee: invoice.initiationFee.toString(),
      otherFee: invoice.otherFee.toString(),
      workHours: invoice.workHours.toString(),
      memberTypeName: invoice.memberTypeName,
      memberTypeAmount: invoice.memberTypeAmount.toString(),
      discountApplied: invoice.discountApplied.toString(),
      discountReason: invoice.discountReason,
      notes: invoice.notes
    });
    setInvoiceFormMode('edit');
    setEditingInvoiceId(invoice.id);
    setSelectedInvoiceRow(rowIndex);
    setFieldErrors((prev) => prev.filter((error) => !error.startsWith('invoices:')));
  };

  const validateInvoiceForm = (): boolean => {
    const errors: string[] = [];

    // Helper function to validate numeric fields (allows blank values)
    const validateNumericField = (value: string, fieldName: string): boolean => {
      // Allow blank values
      if (value === '' || value === null || value === undefined) {
        return true;
      }

      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) {
        errors.push(`invoices:${fieldName}`);
        return false;
      }
      return true;
    };

    // Helper function to validate integer fields (allows blank values)
    const validateIntegerField = (value: string, fieldName: string): boolean => {
      // Allow blank values
      if (value === '' || value === null || value === undefined) {
        return true;
      }

      const intValue = parseInt(value);
      if (isNaN(intValue) || intValue < 0) {
        errors.push(`invoices:${fieldName}`);
        return false;
      }
      return true;
    };

    // Validate all numeric fields
    validateNumericField(invoiceFormData.dues, 'dues');
    validateNumericField(invoiceFormData.lateFee, 'lateFee');
    validateNumericField(invoiceFormData.initiationFee, 'initiationFee');
    validateNumericField(invoiceFormData.otherFee, 'otherFee');
    validateNumericField(invoiceFormData.memberTypeAmount, 'memberTypeAmount');
    validateNumericField(invoiceFormData.discountApplied, 'discountApplied');

    // Validate integer fields
    validateIntegerField(invoiceFormData.workHours, 'workHours');

    // Add errors to existing field errors
    setFieldErrors((prev) => [
      ...prev.filter((error) => !error.startsWith('invoices:')),
      ...errors
    ]);
    return errors.length === 0;
  };

  const saveInvoice = async (): Promise<void> => {
    if (!validateInvoiceForm() || !editingInvoiceId) {
      return;
    }

    setSavingInvoice(true);

    try {
      // Find the original invoice to preserve all fields
      const originalInvoice = invoices.find((inv) => inv.id === editingInvoiceId);
      if (!originalInvoice) {
        toaster.addToast('Original invoice not found', ToastStyle.ERROR, 3000);
        return;
      }

      // Helper function to convert blank values to 0
      const parseFloatOrZero = (value: string): number => {
        if (value === '' || value === null || value === undefined) {
          return 0;
        }
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
      };

      const parseIntOrZero = (value: string): number => {
        if (value === '' || value === null || value === undefined) {
          return 0;
        }
        const parsed = parseInt(value);
        return isNaN(parsed) ? 0 : parsed;
      };

      // Create the complete invoice object with updated values
      const updateRequest: IAnnualInvoiceDto = {
        ...originalInvoice,
        dues: parseFloatOrZero(invoiceFormData.dues),
        lateFee: parseFloatOrZero(invoiceFormData.lateFee),
        initiationFee: parseFloatOrZero(invoiceFormData.initiationFee),
        otherFee: parseFloatOrZero(invoiceFormData.otherFee),
        workHours: parseIntOrZero(invoiceFormData.workHours),
        memberTypeName: invoiceFormData.memberTypeName,
        memberTypeAmount: parseFloatOrZero(invoiceFormData.memberTypeAmount),
        discountApplied: parseFloatOrZero(invoiceFormData.discountApplied),
        discountReason: invoiceFormData.discountReason,
        notes: invoiceFormData.notes
      };

      const endpoint = `/members/${clubMemberId}/invoices/${editingInvoiceId}`;
      const apiResponse = await api.put<IAnnualInvoiceResult>(
        endpoint,
        JSON.stringify(updateRequest)
      );

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response?.succeeded) {
        toaster.addToast('Invoice updated successfully', ToastStyle.SUCCESS, 3000);
        clearInvoiceForm();
        await fetchInvoices(clubMemberId);
      } else {
        const errorMessage = apiResponse?.response?.message || 'Failed to update invoice';
        toaster.addToast(errorMessage, ToastStyle.ERROR, 3000);
      }
    } catch (error) {
      toaster.addToast('Error updating invoice', ToastStyle.ERROR, 3000);
    } finally {
      setSavingInvoice(false);
    }
  };

  const generateInvoice = async (): Promise<void> => {
    setGeneratingInvoice(true);

    try {
      const addRequest: IAnnualInvoiceAddRequest = {
        clubMemberId: parseInt(clubMemberId)
      };

      const endpoint = `/members/${clubMemberId}/invoices`;
      const apiResponse = await api.post<IAnnualInvoiceResult>(
        endpoint,
        JSON.stringify(addRequest)
      );

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response?.succeeded) {
        toaster.addToast('Invoice generated successfully', ToastStyle.SUCCESS, 3000);
        await fetchInvoices(clubMemberId);
      } else {
        const errorMessage = apiResponse?.response?.message || 'Failed to generate invoice';
        toaster.addToast(errorMessage, ToastStyle.ERROR, 3000);
      }
    } catch (error) {
      toaster.addToast('Error generating invoice', ToastStyle.ERROR, 3000);
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const recalculateInvoice = async (): Promise<void> => {
    if (!editingInvoiceId) {
      toaster.addToast('Please select an invoice to recalculate', ToastStyle.ERROR, 3000);
      return;
    }

    setGeneratingInvoice(true);

    try {
      const endpoint = `/members/${clubMemberId}/invoices/${editingInvoiceId}/recalculate`;
      const apiResponse = await api.post<IAnnualInvoiceDto>(endpoint, JSON.stringify({}));

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
        const recalculatedInvoice = apiResponse.response;

        // Only update the automatically calculated fields, preserve manually entered fees
        // Calculate the new total amount based on recalculated values + current manual fees
        // Note: Dues is already the discounted amount, so we do NOT subtract discountApplied
        // (discountApplied is a percentage like 25, not a dollar amount)
        const newDues = recalculatedInvoice.dues;
        const currentLateFee = parseFloat(invoiceFormData.lateFee) || 0;
        const newInitiationFee = recalculatedInvoice.initiationFee;
        const currentOtherFee = parseFloat(invoiceFormData.otherFee) || 0;

        const totalAmount = (newDues + currentLateFee + newInitiationFee + currentOtherFee).toFixed(
          2
        );

        // Update only the recalculated fields, keep manual fees unchanged
        // Note: Initiation fee is also recalculated based on member's first joined date
        setInvoiceFormData((prev) => ({
          ...prev, // Keep all existing values
          totalAmount: totalAmount,
          dues: recalculatedInvoice.dues.toString(),
          initiationFee: recalculatedInvoice.initiationFee.toString(),
          workHours: recalculatedInvoice.workHours.toString(),
          memberTypeName: recalculatedInvoice.memberTypeName,
          memberTypeAmount: recalculatedInvoice.memberTypeAmount.toString(),
          discountApplied: recalculatedInvoice.discountApplied.toString(),
          discountReason: recalculatedInvoice.discountReason
          // Late Fee, Other Fee, Notes remain unchanged
        }));

        toaster.addToast(
          'Invoice values recalculated. Review the changes and click Save to keep them.',
          ToastStyle.SUCCESS,
          5000
        );
      } else {
        const errorMessage =
          apiResponse?.errors?.[0]?.description || 'Failed to recalculate invoice';
        toaster.addToast(errorMessage, ToastStyle.ERROR, 3000);
      }
    } catch (error) {
      toaster.addToast('Error recalculating invoice', ToastStyle.ERROR, 3000);
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const printInvoice = () => {
    if (invoiceFormMode === 'disabled' || !clubMember || !editingInvoiceId) {
      toaster.addToast('Please select an invoice to print', ToastStyle.ERROR, 3000);
      return;
    }

    // Get member name
    const primaryMember = clubMember.primaryMember;
    const memberName = [
      primaryMember.title ? MemberTitle[primaryMember.title] : '',
      primaryMember.firstName,
      primaryMember.middleName,
      primaryMember.lastName,
      primaryMember.suffix ? MemberSuffix[primaryMember.suffix] : ''
    ]
      .filter(Boolean)
      .join(' ');

    // Create title
    const title = `Invoice for ${memberName} - ${invoiceFormData.billingYear}`;

    // Get logo path - assuming it's in the public folder
    const logoPath = '/logo-dark.png'; // Adjust path as needed

    // Format current date
    const currentDate = new Date();
    const formattedCurrentDate = Helpers.toShortDate(currentDate);

    // Format money values
    const formatMoney = (value: number): string => {
      return `$${value.toFixed(2)}`;
    };

    // Calculate values
    const dues = parseFloat(invoiceFormData.dues) || 0;
    const lateFee = parseFloat(invoiceFormData.lateFee) || 0;
    const initiationFee = parseFloat(invoiceFormData.initiationFee) || 0;
    const otherFee = parseFloat(invoiceFormData.otherFee) || 0;
    const discountApplied = parseFloat(invoiceFormData.discountApplied) || 0;
    const memberTypeAmount = parseFloat(invoiceFormData.memberTypeAmount) || 0;

    // Calculate subtotal (dues + lateFee + initiationFee + otherFee)
    // Note: Dues is already the discounted amount, so we do NOT subtract discountApplied
    // (discountApplied is a percentage like 25, not a dollar amount)
    const subTotal = dues + lateFee + initiationFee + otherFee;

    const printHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${title}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              font-size: 1.1em;
              margin: 10px;
            }

            .center {
              text-align: center;
              margin: 20px 0;
            }

            .center img {
              max-width: 300px;
              height: auto;
            }

            h3 {
              font-size: 1.5em;
              margin: 10px 0;
              text-align: center;
            }

            h4 {
              font-size: 1.3em;
              margin: 8px 0;
              text-align: center;
            }

            table {
              width: 650px;
              margin: 20px auto;
              border-collapse: collapse;
            }

            td {
              padding: 5px 10px;
              vertical-align: top;
            }

            .label-column {
              font-weight: bold;
              width: 320px;
            }

            .value-column {
              text-align: left;
            }

            .separator-row td {
              border-top: 1px solid #000;
              font-weight: bold;
              padding-top: 10px;
            }

            .total-row {
              font-weight: bold;
              font-size: 1.3em;
            }

            @media print {
              body {
                margin: 0;
                font-size: 14px;
              }

              table {
                width: 600px;
              }

              td {
                padding: 3px 8px;
              }

              .center img {
                max-width: 250px;
              }
            }
          </style>
        </head>
        <body>
          <div class="center">
            <img src="${logoPath}" alt="TRGC Logo" />
          </div>

          <h3>Invoice for: ${memberName}</h3>
          <h4>Billing Year: ${invoiceFormData.billingYear}</h4>

          <table>
            <tr>
              <td class="label-column">Invoice Date:</td>
              <td class="value-column">${formattedCurrentDate}</td>
            </tr>
            <tr>
              <td class="label-column">Invoice ID:</td>
              <td class="value-column">${invoiceFormData.id}</td>
            </tr>
            <tr><td colspan="2">&nbsp;</td></tr>

            <tr>
              <td class="label-column">Member Type:</td>
              <td class="value-column">${invoiceFormData.memberTypeName || ''}</td>
            </tr>
            <tr>
              <td class="label-column">Volunteer Work Hours:</td>
              <td class="value-column">${invoiceFormData.workHours || '0'}</td>
            </tr>
            <tr><td colspan="2">&nbsp;</td></tr>

            <tr>
              <td class="label-column">Base Dues for Membership Type:</td>
              <td class="value-column">${formatMoney(memberTypeAmount)}</td>
            </tr>
            <tr>
              <td class="label-column">Discount Applied:</td>
              <td class="value-column">${discountApplied > 0 ? `${discountApplied.toFixed(2)}%` : '0%'}</td>
            </tr>
            <tr>
              <td class="label-column">Discount Reason:</td>
              <td class="value-column">${invoiceFormData.discountReason || ''}</td>
            </tr>
            <tr><td colspan="2">&nbsp;</td></tr>

            <tr>
              <td class="label-column">Dues:</td>
              <td class="value-column">${formatMoney(dues)}</td>
            </tr>
            <tr>
              <td class="label-column">Initiation Fee:</td>
              <td class="value-column">${formatMoney(initiationFee)}</td>
            </tr>
            <tr>
              <td class="label-column">Late Fee:</td>
              <td class="value-column">${formatMoney(lateFee)}</td>
            </tr>
            ${
              otherFee > 0
                ? `
            <tr>
              <td class="label-column">Other Fee:</td>
              <td class="value-column">${formatMoney(otherFee)}</td>
            </tr>
            `
                : ''
            }
            <tr class="separator-row">
              <td class="label-column">Total Amount:</td>
              <td class="value-column">${formatMoney(subTotal)}</td>
            </tr>
          </table>
        </body>
      </html>
    `;

    // Create a hidden iframe instead of opening a new window
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '1px';
    iframe.style.height = '1px';
    iframe.style.visibility = 'hidden';

    // Add the iframe to the document
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) {
      document.body.removeChild(iframe);
      toaster.addToast('Error creating print preview', ToastStyle.ERROR, 3000);
      return;
    }

    // Write the print content to the iframe
    iframeDoc.write(printHtml);
    iframeDoc.close();

    // Wait for the iframe to load, then trigger print
    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();

        // Clean up the iframe after printing starts
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 1000);
      } catch (error) {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
        toaster.addToast('Print failed', ToastStyle.ERROR, 3000);
      }
    };

    // Fallback cleanup in case onload doesn't fire
    setTimeout(() => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }, 5000);
  };

  const handleInvoiceSort = (sortField: string) => {
    if (invoiceSortField === sortField) {
      // Toggle direction
      setInvoiceSortDirection(
        invoiceSortDirection === SortDirection.ASCENDING
          ? SortDirection.DESCENDING
          : SortDirection.ASCENDING
      );
    } else {
      // New field, default to ascending
      setInvoiceSortField(sortField);
      setInvoiceSortDirection(SortDirection.ASCENDING);
    }
  };

  const getSortedInvoices = (): IAnnualInvoiceDto[] => {
    return [...invoices].sort((a, b) => {
      let aValue: string | number | Date;
      let bValue: string | number | Date;

      // Handle each field type explicitly for type safety
      switch (invoiceSortField) {
        case 'totalAmount':
          // Note: Dues is already the discounted amount, so we do NOT subtract discountApplied
          aValue = a.dues + a.lateFee + a.initiationFee + a.otherFee;
          bValue = b.dues + b.lateFee + b.initiationFee + b.otherFee;
          break;
        case 'billingYear':
        case 'dues':
        case 'lateFee':
        case 'initiationFee':
        case 'otherFee':
        case 'workHours':
        case 'memberTypeAmount':
        case 'discountApplied':
          aValue = a[invoiceSortField];
          bValue = b[invoiceSortField];
          break;
        case 'memberTypeName':
        case 'discountReason':
        case 'notes':
        case 'createdBy':
        case 'lastModifiedBy':
          aValue = (a[invoiceSortField] || '').toLowerCase();
          bValue = (b[invoiceSortField] || '').toLowerCase();
          break;
        case 'dateCreatedUtc':
          aValue = a.dateCreatedUtc.getTime();
          bValue = b.dateCreatedUtc.getTime();
          break;
        case 'lastModifiedDateUtc':
          aValue = a.lastModifiedDateUtc ? a.lastModifiedDateUtc.getTime() : 0;
          bValue = b.lastModifiedDateUtc ? b.lastModifiedDateUtc.getTime() : 0;
          break;
        default:
          // Fallback for any other fields
          aValue = String(a[invoiceSortField as keyof IAnnualInvoiceDto] || '');
          bValue = String(b[invoiceSortField as keyof IAnnualInvoiceDto] || '');
      }

      if (aValue < bValue) {
        return invoiceSortDirection === SortDirection.ASCENDING ? -1 : 1;
      }
      if (aValue > bValue) {
        return invoiceSortDirection === SortDirection.ASCENDING ? 1 : -1;
      }
      return 0;
    });
  };

  const canGenerateInvoice = (): boolean => {
    const currentBillingYear = getCurrentBillingYear();
    return !invoices.some((invoice) => invoice.billingYear === currentBillingYear);
  };

  return {
    // State
    invoices,
    loadingInvoices,
    savingInvoice,
    generatingInvoice,
    invoiceFormData,
    invoiceFormMode,
    editingInvoiceId,
    selectedInvoiceRow,
    invoiceSortField,
    invoiceSortDirection,

    // Actions
    updateInvoiceFormData,
    clearInvoiceForm,
    selectInvoiceForEdit,
    saveInvoice,
    generateInvoice,
    recalculateInvoice,
    printInvoice,
    handleInvoiceSort,
    getSortedInvoices,
    canGenerateInvoice,
    refetchInvoices: fetchInvoices
  };
};
